import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { Location, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";

// region Icons
import { LinkContract } from "@assets/icons";
// endregion Icons
// region Molecules
import { BaseCard, BaseCardHeader, LinkContractDialog, PageHeader } from "@molecules/index";
// endregion Molecules
// region Store
import Store, { ContractPermissions } from "@store/jotai";
// endregion Store
// region Telluria
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
// endregion Telluria
// region Atoms
import { Loading } from "@atoms/index";
// endregion Atoms
// region Graphql
import {
  ConstructionEmailQueriesGetAllWithPaginationArgs,
  ECommandResultStatus,
  EWhereClausesOperators,
  PermissionUserQueriesGetAllWithoutPaginationArgs,
  UserType
} from "@store/graphql/helpers/types.graphql";
import {
  permissionUserAllWithoutPagination,
  TPermissionAllWithoutPaginationQueries
} from "@graphql/groups/permissionUser/query.graphql";
import {
  constructionEmailAllWithPagination,
  TConstructionEmailAllWithPaginationQueries
} from "@graphql/groups/constructionEmail/query.graphql";

import { ReactSVG } from "react-svg";
// endregion Graphql
// region Libraries
import { Grid } from "@libraries/mui";
// endregion Libraries
// region Language
import { PermissionMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";

import * as Styled from "./styles";
import Office from "../Office";

// endregion Language

interface LocationProps extends Location {
  state: {
    userSelected: UserType;
  };
}

const PermissionUser: React.FC = () => {

  const location = useLocation();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { setValue: setClients } = Store.useClients();
  const {
    value: permissionsUsers,
    setValue: setPermissionsUsers,
    filteredPermissionsUsers,
    setFilteredPermissionsUsers
  } = Store.usePermissionsUsers();

  const { state } = location as LocationProps;
  const { userSelected } = state;

  // Link contract Dialog State
  const [linkContractDialogOpen, setLinkContractDialogOpen] = useState(false);
  const handleLinkContractDialog = useCallback(() => {
    setLinkContractDialogOpen(!linkContractDialogOpen);
  }, [linkContractDialogOpen]);

  // region Get permission of selected user
  const {
    loading: loadingPermissions,
    error: errorPermissions,
    data: dataPermissions
  } = useQuery<
    TPermissionAllWithoutPaginationQueries,
    PermissionUserQueriesGetAllWithoutPaginationArgs
  >(
    permissionUserAllWithoutPagination([
      "id",
      "contract.id",
      "contract.code",
      "contract.status",
      "contract.contractDate",
      "contract.construction.id",
      "contract.construction.name",
      "contract.construction.address",
      "contract.construction.county",
      "contract.construction.number",
      "contract.construction.complement",
      "contract.construction.client.name",
      "module.id",
      "module.description"
    ]),
    {
      fetchPolicy: "network-only",
      variables: {
        where: [
          {
            property: "user.id",
            operator: EWhereClausesOperators.EQUALS,
            value: userSelected.id
          }
        ]
      }
    }
  );
  // endregion Get permission of selected user
  // region Get construction emails of selected user
  const {
    loading: loadingConstructionEmails,
    error: errorConstructionEmails,
    data: dataConstructionEmails
  } = useQuery<
    TConstructionEmailAllWithPaginationQueries,
    ConstructionEmailQueriesGetAllWithPaginationArgs
  >(
    constructionEmailAllWithPagination([
      "id",
      "construction.contracts.id",
      "construction.contracts.code",
      "construction.contracts.status",
      "construction.contracts.contractDate",
      "construction.id",
      "construction.name",
      "construction.address",
      "construction.county",
      "construction.number",
      "construction.complement",
      "construction.client.name"
    ]),
    {
      fetchPolicy: "network-only",
      variables: {
        where: [
          {
            property: "email",
            operator: EWhereClausesOperators.EQUALS,
            value: userSelected.email
          }
        ]
      }
    }
  );
  // endregion Get construction emails of selected user

  /**
   * Load permissions users according data and construction emails permissions
   */
  const loadPermissionsUsers = useCallback(async () => {

    const responsePermissions = dataPermissions?.permissionUser.getAllWithoutPagination;
    const responseConstructionEmails = dataConstructionEmails?.constructionEmail.getAllWithPagination;

    if (
      responsePermissions?.status === ECommandResultStatus.SUCCESS
      && responseConstructionEmails?.status === ECommandResultStatus.SUCCESS
    ) {

      const permissionsArray = _.uniqBy(
        responsePermissions?.result?.map((el) => el?.contract),
        "id"
      ) as ContractPermissions[];

      const contractsWithPermissions = permissionsArray.map((contract) => {
        const permissions = responsePermissions.result?.filter((permission) => permission?.contract?.id === contract.id);

        return { ...contract, permissions, userId: userSelected.id };
      });

      const contractsByContructions = responseConstructionEmails?.result?.map(
        (constructionEmail) => constructionEmail?.construction?.contracts?.map((contract) => ({
          ...contract,
          construction: constructionEmail.construction
        }))
      );

      const contractsByConstructionsFlatten = _.flatten(
        contractsByContructions
      );

      const contractsByConstructionsWithPermissions = contractsByConstructionsFlatten
        .map((contract) => {
          const contractWithPermissionFinded = contractsWithPermissions.find(
            (contractWithPermission) => contractWithPermission.id === contract?.id
          );

          if (contractWithPermissionFinded) return undefined;

          return { ...contract, permissions: [], userId: userSelected.id };
        })
        .filter(
          (contract) => contract !== undefined
        ) as ContractPermissions[];

      const contractsWithPermissionsNested = [
        ...contractsWithPermissions,
        ...contractsByConstructionsWithPermissions
      ];

      const contractsWithPermissionsOrderedByDate = _.orderBy(
        contractsWithPermissionsNested,
        ["contractDate"],
        ["desc"]
      );

      setPermissionsUsers(contractsWithPermissionsOrderedByDate);
    }
  }, [
    dataPermissions,
    setPermissionsUsers,
    userSelected,
    dataConstructionEmails
  ]);

  // Verify if it is loading
  const isLoading = useMemo(
    () => loadingPermissions || loadingConstructionEmails,
    [loadingConstructionEmails, loadingPermissions]
  );

  // Load permissions users after get data and construction emails
  useEffect(() => {
    if (dataPermissions && dataConstructionEmails) loadPermissionsUsers().then();
    if (errorPermissions) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorPermissions.message
      });
    }
    if (errorConstructionEmails) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorConstructionEmails.message
      });
    }
  }, [
    loadPermissionsUsers,
    dataPermissions,
    errorPermissions,
    addToast,
    dataConstructionEmails,
    errorConstructionEmails
  ]);

  // Set filtered permissions users
  useEffect(() => {
    setFilteredPermissionsUsers(permissionsUsers);
  }, [permissionsUsers, setFilteredPermissionsUsers]);

  // Reset permissions users, clients and filtered permissions users
  useEffect(() => () => {
    setPermissionsUsers([]);
    setFilteredPermissionsUsers([]);
    setClients([]);
  }, [setFilteredPermissionsUsers, setPermissionsUsers, setClients]);

  return (
    <Office showSelectClient={false}>
      <PageHeader
        url="/permissions"
        title={`${t(PermissionMessages.permissionsForText)}
          ${userSelected.fullName} ${userSelected.clientName ? ` (${userSelected.clientName})` : ""}
        `}
      />
      <Styled.Container>
        <Grid container spacing={2}>
          {isLoading && (
            <Grid item xs={12}>
              <Loading loading />
            </Grid>
          )}
          {!isLoading
            && filteredPermissionsUsers.map((permission) => (
              <Grid key={`grid-${permission.id}`} item xs={12} sm={6} md={4}>
                <BaseCard
                  key={permission.id}
                  id={permission.id}
                  header={
                    <BaseCardHeader type="permissionUser" data={permission} />
                    }
                />
              </Grid>
            ))}
        </Grid>
      </Styled.Container>
      <Styled.LinkContractButtonContent>
        <Styled.LinkContractButton
          variant="contained"
          size="small"
          type="submit"
          text={t(PermissionMessages.linkContractToUserText).toUpperCase()}
          onClick={handleLinkContractDialog}
          endIcon={<ReactSVG src={LinkContract} />}
        />
      </Styled.LinkContractButtonContent>
      <LinkContractDialog
        open={linkContractDialogOpen}
        onClose={handleLinkContractDialog}
        title={t(PermissionMessages.linkContractToUserText)}
        userSelected={userSelected}
      />
    </Office>
  );
};

export default PermissionUser;
